import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";

const LAST_COMPANY_KEY = "bbqsync_last_company";

/** Σελίδα ενεργοποίησης πρόσκλησης (/invite/:token): ο προσκεκλημένος ορίζει
 *  username/κωδικό — ή συνδέεται με Google με το ΙΔΙΟ email. Τα στοιχεία και τα
 *  δικαιώματα τα έχει ήδη ορίσει ο διαχειριστής του καταστήματος. */
export default function InvitePage() {
  const { t, i18n } = useTranslation();
  const { token } = useParams();
  const [state, setState] = useState("checking"); // checking | form | invalid | done
  const [info, setInfo] = useState(null);
  const [username, setUsername] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const googleBtnRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${config.API_BASE_URL}/public/auth/invite/${token}`)
      .then(({ data }) => {
        setInfo(data);
        setUsername(data.suggestedUsername || "");
        setState("form");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const finishLogin = useCallback(async (user, companySlug) => {
    try {
      const { data } = await axios.get(`${config.API_BASE_URL}/csrf-token`, { withCredentials: true });
      axios.defaults.headers.common["X-CSRF-Token"] = data.csrfToken;
      sessionStorage.setItem("csrfToken", data.csrfToken);
    } catch {}
    try {
      localStorage.setItem("user", JSON.stringify(user));
      if (companySlug) localStorage.setItem(LAST_COMPANY_KEY, companySlug);
    } catch {}
    setState("done");
    setTimeout(() => window.location.assign("/"), 1200);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setError(t("The two passwords do not match."));
    if (pw.length < 8) return setError(t("Password must be at least 8 characters."));
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(
        `${config.API_BASE_URL}/public/auth/invite/accept`,
        { token, username: username.trim(), password: pw },
        { withCredentials: true }
      );
      await finishLogin(data.user, data.company);
    } catch (err) {
      const code = err?.response?.data?.error;
      setError(
        code === "username-taken" ? t("This username is already taken in this store — pick another.")
        : code === "bad-username" ? t("Username: 3-45 characters, letters/numbers/._@- only.")
        : code === "token-invalid-or-expired" ? t("This link has expired or was already used.")
        : t("Something went wrong — please try again.")
      );
    } finally { setBusy(false); }
  };

  const handleGoogleCredential = useCallback(async (credential) => {
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(
        `${config.API_BASE_URL}/public/auth/invite/google`,
        { token, credential },
        { withCredentials: true }
      );
      await finishLogin(data.user, data.company);
    } catch (err) {
      const code = err?.response?.data?.error;
      setError(
        code === "email-mismatch" ? t("Please use the Google account of the email address this invitation was sent to.")
        : code === "token-invalid-or-expired" ? t("This link has expired or was already used.")
        : code === "invalid-google-token" ? t("Google sign-in failed — please try again.")
        : t("Something went wrong — please try again.")
      );
    } finally { setBusy(false); }
  }, [token, finishLogin, t]);

  // Google Identity Services (ίδιο pattern με LoginPage/BuzzSignup)
  useEffect(() => {
    if (state !== "form") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${config.API_BASE_URL}/public/buzz/auth-config`);
        if (cancelled || !data.googleClientId) return;
        const lang = (i18n.resolvedLanguage || i18n.language || "el").startsWith("el") ? "el" : "en";
        const init = () => {
          if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;
          window.google.accounts.id.initialize({
            client_id: data.googleClientId,
            callback: (resp) => handleGoogleCredential(resp.credential),
          });
          // Ίδιο στυλ με το κουμπί Google του login: pill + πλάτος όσο η φόρμα
          const w = Math.min(400, Math.max(220, googleBtnRef.current.parentElement?.offsetWidth
            || googleBtnRef.current.offsetWidth || 300));
          window.google.accounts.id.renderButton(googleBtnRef.current, {
            theme: "outline", size: "large", width: w, shape: "pill",
            text: "continue_with", locale: lang, logo_alignment: "center",
          });
        };
        if (window.google?.accounts?.id) return init();
        const s = document.createElement("script");
        s.src = "https://accounts.google.com/gsi/client";
        s.async = true;
        s.onload = init;
        document.head.appendChild(s);
      } catch { /* χωρίς Google — μόνο username/κωδικός */ }
    })();
    return () => { cancelled = true; };
  }, [state, handleGoogleCredential, i18n.language, i18n.resolvedLanguage]);

  const S = {
    wrap: { maxWidth: 430, margin: "60px auto", padding: "0 20px", fontFamily: "system-ui, sans-serif" },
    input: { width: "100%", padding: 12, fontSize: 15, borderRadius: 10, border: "1px solid #d1d5db",
             margin: "6px 0", boxSizing: "border-box" },
    btn: { width: "100%", padding: 13, fontSize: 15, fontWeight: 700, borderRadius: 10, border: "none",
           background: "#d97706", color: "#fff", cursor: "pointer", marginTop: 10 },
    hint: { color: "#6b7280", fontSize: 14 },
    err: { background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5",
           borderRadius: 10, padding: "10px 14px", fontSize: 14, margin: "10px 0" },
    chip: { display: "inline-block", background: "#f3f4f6", borderRadius: 8, padding: "3px 10px",
            fontSize: 13, margin: "2px 4px 2px 0", color: "#374151" },
    card: { background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12,
            padding: "12px 16px", margin: "14px 0" },
  };

  if (state === "checking") return <div style={S.wrap}><p style={S.hint}>…</p></div>;

  if (state === "invalid") {
    return (
      <div style={S.wrap}>
        <h2>{t("Invitation link")}</h2>
        <div style={S.err}>{t("This link has expired or was already used.")}</div>
        <p style={S.hint}>{t("Ask your manager to send you a new invitation.")}</p>
      </div>
    );
  }

  if (state === "done") {
    return (
      <div style={S.wrap}>
        <h2>✅ {t("Your account is ready!")}</h2>
        <p style={S.hint}>{t("Signing you in…")}</p>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <h2>{t("Welcome to EstiaSync!")}</h2>
      <p>
        {t("You were invited to join")} <b>{info.business}</b>
        {info.name ? <> — {t("account for")} <b>{info.name}</b></> : null}.
      </p>
      {info.permissions?.length > 0 && (
        <div style={S.card}>
          <div style={{ ...S.hint, marginBottom: 6 }}>{t("Your access")}:</div>
          {info.permissions.map((p) => <span key={p} style={S.chip}>{p}</span>)}
        </div>
      )}
      <p style={S.hint}>
        {t("Choose a username and password — or continue with the Google account of")} <b>{info.maskedEmail}</b>.
      </p>
      {error && <div style={S.err}>{error}</div>}
      <form onSubmit={submit}>
        <label style={S.hint}>{t("Username")}</label>
        <input style={S.input} value={username} onChange={(e) => setUsername(e.target.value)}
               autoCapitalize="none" autoCorrect="off" required minLength={3} maxLength={45} />
        <label style={S.hint}>{t("Password")}</label>
        <input style={S.input} type="password" value={pw} onChange={(e) => setPw(e.target.value)}
               required minLength={8} autoComplete="new-password" />
        <label style={S.hint}>{t("Confirm password")}</label>
        <input style={S.input} type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
               required minLength={8} autoComplete="new-password" />
        <button style={S.btn} type="submit" disabled={busy}>
          {busy ? t("Please wait…") : t("Activate account")}
        </button>
      </form>
      <div ref={googleBtnRef} style={{ display: "flex", justifyContent: "center", marginTop: 14 }} />
    </div>
  );
}
